import { dateClassic, formatDate, formatToUTC, isValidFuturDate, isValidFuturDateLimit, newUTCToday } from '~/helpers/date';

export const useInputDate = (date: string) => {
  const { t } = useI18n();

  const dateModel = ref(date);

  const datePickerInputModel = ref(dateClassic(date));

  const datePickerInputError = ref<boolean>(false);
  const datePickerInputErrorMessage = computed(() => getErrorMessage(datePickerInputModel.value));

  const autoCompleteDateFormatWithSlashes = (value: string) => {
    if (value.endsWith('//')) {
      return value.slice(0, -1);
    }

    if (!value.endsWith('/') && isNaN(Number(value.slice(-1)))) {
      return value.slice(0, -1); // Remove the last character if it's not a number or slash
    }

    if (value.length === 2) {
      return `${value}/`;
    }

    if (value.length === 5) {
      return `${value}/`;
    }

    return value;
  };

  const getErrorMessage = (date: string) => {
    const formatedDate = formatDate(date);

    // Incase we have an invalid date (ex: 2025-02-31) we force the date to be today, so to get an error message for it, we check if we have an error and if the forced date is today.
    if (!isValidDate(formatedDate) || (datePickerInputError.value && formatedDate === newUTCToday())) {
      return t('ux.molecules.searchEngineModalContent.errors.invalidDate');
    }

    if (!isValidFuturDate(formatedDate)) {
      return t('ux.molecules.searchEngineModalContent.errors.pastDate', { date: useDateFormat(new Date(), 'dd/MM/y') });
    }

    if (isValidFuturDate(formatedDate) && !isValidFuturDateLimit(formatedDate, 365)) {
      return t('ux.molecules.searchEngineModalContent.errors.futurDate', { date: useDateFormat(formatToUTC(newUTCToday(), 365), 'dd/MM/y') });
    }

    return null;
  };

  const updateValidateInputDate = (value: string, clear?: boolean): boolean => {
    if (isValidDate(value) && isValidFuturDate(value) && isValidFuturDateLimit(value, 365)) {
      datePickerInputError.value = false;
      dateModel.value = value;

      if (clear) {
        datePickerInputModel.value = dateClassic(value);
      }

      return true;
    } else {
      datePickerInputError.value = true;

      if (!isValidDate(value) && !clear) {
        datePickerInputModel.value = dateClassic(newUTCToday());
      }

      return false;
    }
  };

  const handleInputDateChange = (value: string) => {
    datePickerInputModel.value = autoCompleteDateFormatWithSlashes(value);
  };

  const isValidDate = (value: string): boolean => {
    const [year, month, day] = value.split('-').map((el) => Number(el));
    const date = new Date(year, month - 1, day);
    return date.getFullYear() === year && date.getMonth() + 1 === month && date.getDate() === day;
  };

  return { dateModel, datePickerInputError, datePickerInputErrorMessage, datePickerInputModel, handleInputDateChange, updateValidateInputDate };
};
